import React from 'react';
import { useTranslation } from 'react-i18next';
import { renderCountryFlag } from '../../utils/flagUtils';

interface PackageDetails {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  features: string[];
  processingTime?: string;
  priceNote?: string;
  popular?: boolean;
  country: {
    name: string;
    code: string;
    flag: string;
  };
}

interface PackagePreviewCardProps {
  package: PackageDetails;
  className?: string;
  showChangeButton?: boolean;
  onChangePackage?: () => void;
}

const PackagePreviewCard: React.FC<PackagePreviewCardProps> = ({
  package: pkg,
  className = "",
  showChangeButton = false,
  onChangePackage
}) => {
  const { t } = useTranslation();

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border-2 border-primary-200 p-6 ${className}`}>
      {/* Header with country flag and popular badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div>
            {renderCountryFlag(
              pkg.country.code,
              pkg.country.flag,
              pkg.country.name,
              'w-12 h-9 object-cover rounded-sm'
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{pkg.name}</h3>
            <p className="text-sm text-gray-600">{pkg.country.name}</p>
          </div>
        </div>
        {!!pkg.popular && (
          <span className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
            Popular
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-gray-700 mb-4 text-sm">{pkg.description}</p>

      {/* Price */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-2xl font-bold text-primary-600">
            {formatPrice(pkg.price, pkg.currency)}
          </span>
          {pkg.priceNote && (
            <p className="text-xs text-gray-500 mt-1">{pkg.priceNote}</p>
          )}
          {pkg.processingTime && (
            <p className="text-xs text-gray-500 mt-1">
              Timeline: {pkg.processingTime}
            </p>
          )}
        </div>
        {showChangeButton && (
          <button
            onClick={onChangePackage}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium underline"
          >
            Change Package
          </button>
        )}
      </div>

      {/* Features */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Included Features:</h4>
        <ul className="space-y-1">
          {pkg.features.slice(0, 4).map((feature, idx) => (
            <li key={idx} className="flex items-start text-sm">
              <svg
                className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
          {pkg.features.length > 4 && (
            <li className="text-sm text-gray-500 ml-6">
              +{pkg.features.length - 4} more features
            </li>
          )}
        </ul>
      </div>

      {/* Border accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-t-xl"></div>
    </div>
  );
};

export default PackagePreviewCard;
